import React, { useEffect } from "react";
import styles from "./BaufiImplecoLeadPage.scss";
import styles2 from "./FinancialCheckPage.scss";
import cx from "../../utils/cx";
import LoanData from "./LoanData";
import { IFinancialCheckData, INextStepData } from "./FinancialCheckPage";
import JsonApiRequest, { IContact, IProperty, IUser } from "../../utils/JsonApiRequest";
import FirstPersonData from "./FirstPersonData";
import Result from "./Result";
import ThankYou from "./ThankYou";
import useUser from "../../stores/useUser";
import { ILeadData } from "../../utils/LeadSmartApi";
import trackApi from "../../utils/trackApi";
import CheckCircle from "../svg/CheckCircleOutlined";

export interface IBaufiImplecoLeadPageProps {
    className?: string;
    property?: IProperty;
    variant?: 'default' | 'propertyMessage';
}

export const DEFAULT_PRICE = 300000;

const setHash = (hash: string) => {
    if (hash) {
        location.hash = hash;
    } else {
        // clear hash and fire hashchange event
        history.pushState("", document.title, window.location.pathname + window.location.search);
        window.dispatchEvent(new HashChangeEvent('hashchange'));
    }
};


const steps = [
    {
        id: '',
        trackingName: 'Start',
        component: LoanData,
    },
    {
        id: 'firstPersonData',
        trackingName: 'InfosPerson',
        component: FirstPersonData,
    },
    {
        id: 'result',
        trackingName: 'Ergebnis',
        component: Result,
    },
    {
        id: 'thankYou', // only when terms.email was false in firstPersonData
        trackingName: 'Ende',
        component: ThankYou,
    }
]

// const testData = {

//     "firstPerson": {
//         "firstName": "Max",
//         "lastName": "Mustermann",
//         "email": "mamu@esample.com",
//         "postalCode": "76135",
//         "birthday": "1977-07-07",
//         "typeOfEmployment": "ANGESTELLTER",
//         "employedSince": "2025-02-01",
//         "monthlyNetIncomeInEuro": 3000,
//         "additionalMonthlyIncome": 500
//     },
// }

interface IAdditionalFields {
    typeOfEmployment: IFinancialCheckData['firstPerson']['typeOfEmployment'];
    additionalMonthlyIncome: number
    postalCode: string;
}


export type IPersonFields = Omit<ILeadData['firstPerson'], 'employmentType'> & IAdditionalFields;



const BaufiImplecoLeadPage: React.FC<IBaufiImplecoLeadPageProps> = ({ className = '', property, variant }) => {

    const [activeStep, setActiveStep] = React.useState(0);
    const [isLoading, setIsLoading] = React.useState(true);
    // const [homeData, setHomeData] = React.useState<IProperty>(null);
    const [data, setData] = React.useState<IFinancialCheckData>({ property } as unknown as IFinancialCheckData);

    const [user] = useUser();

    useEffect(() => {

        // tracking params
        const params = new URLSearchParams(window.location.search);
        const isClickFromMail = params.get('modal') === 'baufilead';
        const isPropertyMessage = variant === 'propertyMessage';
        const streckenzweig1 = isPropertyMessage ? 'Anfrageprozess aus Kaufobjekt' : isClickFromMail ? 'Suchauftrag' : window.location.pathname;

        /**
         * Hash-Change Listener / steps routing
         */
        const updateHash = () => {
            const hash = decodeURIComponent(location.hash.replace('#', ''));
            const step = steps.findIndex(({ id }) => id === hash);
            if (step !== -1) {
                setActiveStep(step);
            } else {
                setActiveStep(0);
            }

            const trackingName = steps[step]?.trackingName || 'Start';
            trackApi.wglPageview(
                `/WOHNGLUECK/service/BAUFINANZIERUNGS-CHECK/${trackingName.toLowerCase()}`,
                `Baufinanzierung-Check ${trackingName}`,
                {streckenzweig1}
            );
        };

        trackApi.wglPageview(
            '/WOHNGLUECK/service/BAUFINANZIERUNGS-CHECK/start',
            'Baufinanzierung-Check Start',
            {streckenzweig1}
        );

        // delete hash on initial load
        history.pushState("", document.title, window.location.pathname + window.location.search);
        addEventListener("hashchange", updateHash);


        return () => {
            removeEventListener("hashchange", updateHash);
        };

    }, []);

    useEffect(() => {

        if (user.isLoggedIn) {

            // load contact data to prefill firstPersonData
            JsonApiRequest.getRequest(`/real_estate_contact/real_estate_contact?filter[user_id.id][value]=${user.id}&sort=drupal_internal__id`)
                .then((data) => {
                    const contactData = JsonApiRequest.data2Object<IContact>(data.getData()).find((e) => true);
                    setData(prev => ({
                        ...prev, firstPerson: {
                            firstName: contactData?.field_address?.given_name,
                            lastName: contactData?.field_address?.family_name,
                            email: contactData?.mail,
                            postalCode: contactData?.field_address?.postal_code,
                            ...prev.firstPerson
                        }
                    }));
                    setIsLoading(false);
                })
                .catch((e) => {
                    console.error(e);
                });

            // load user data to prefill firstPersonData
            JsonApiRequest.getRequest(`/user/user/${user.id}`)
                .then((data) => {
                    const userData = JsonApiRequest.data2Object<IUser>(data.getData()).find((e) => true);
                    setData(prev => ({
                        ...prev, 
                        firstPerson: {
                            birthday: userData?.date_of_birth,
                            typeOfEmployment: userData?.type_of_employment,
                            monthlyNetIncomeInEuro: Number(userData?.net_income),
                            employedSince: userData?.employed_since,
                            additionalMonthlyIncome: Number(userData?.additional_monthly_income),
                            ...prev.firstPerson
                        }
                    }));
                    setIsLoading(false);
                })
                .catch((e) => {
                    console.error(e);
                });
        }

    }, [user]);

    // useEffect(() => {
    //     tracking(steps[activeStep], data.termsData ? !!data.termsData.email : undefined);
    // }, [activeStep]);

    const nextStep = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const nextStep = steps[activeStep + 1]?.id || '';
        setHash(nextStep);
    }

    const handleSetData = (data?: INextStepData) => {
        setData(prev => ({ ...prev, ...data }));
    }

    const prevStep = (data?: { [key in keyof IFinancialCheckData]: IFinancialCheckData[key] }) => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setData(prev => ({ ...prev, ...data }));
        const prevStep = steps[activeStep - 1]?.id || '';
        setHash(prevStep);
    };


    const StepComponent = steps[activeStep]?.component || console.error('no step component for step', activeStep) || steps[0].component;



    return <div className={cx(styles.baufiImplecoLeadPage, styles2.financialCheckPage, className)}>

        {(variant === 'propertyMessage' && activeStep === 0) && <div className={styles.propertyMessageSuccess}>
            <CheckCircle />
            <h2>Super, deine Anfrage wurde versendet.</h2>
        </div>}

        <StepComponent data={data} nextStep={nextStep} setData={handleSetData} prevStep={prevStep} variant={variant}/>

    </div >
};

export default BaufiImplecoLeadPage;
